import { cn } from "@/lib/utils";
import { scorePassword, type StrengthLevel } from "./utils";
import type { OnboardingLabels } from "./types";

export interface PasswordStrengthMeterProps {
  password: string;
  labels: OnboardingLabels["account"]["strength"];
  className?: string;
}

const TONE: Record<StrengthLevel, string> = {
  0: "bg-biz-muted",
  1: "bg-biz-danger",
  2: "bg-amber-500",
  3: "bg-biz-primary",
  4: "bg-biz-success",
};

const LABEL_KEY: Record<Exclude<StrengthLevel, 0>, keyof OnboardingLabels["account"]["strength"]> = {
  1: "weak",
  2: "fair",
  3: "good",
  4: "strong",
};

export function PasswordStrengthMeter({ password, labels, className }: PasswordStrengthMeterProps) {
  const { score } = scorePassword(password);
  const showLabel = score > 0 ? labels[LABEL_KEY[score as 1 | 2 | 3 | 4]] : "";

  return (
    <div
      className={cn("flex items-center gap-3", className)}
      role="status"
      aria-live="polite"
      aria-label={showLabel || "Password strength"}
    >
      <div className="flex flex-1 gap-1.5">
        {[1, 2, 3, 4].map((bar) => (
          <div
            key={bar}
            className={cn(
              "h-1.5 flex-1 rounded-full transition-colors duration-150",
              score >= bar ? TONE[score as StrengthLevel] : "bg-biz-muted",
            )}
          />
        ))}
      </div>
      <span
        className={cn(
          "min-w-[3.5rem] text-right text-xs font-medium tabular-nums",
          score >= 3 ? "text-biz-primary" : score === 2 ? "text-amber-600" : score === 1 ? "text-biz-danger" : "text-biz-foreground-muted",
        )}
      >
        {showLabel || "—"}
      </span>
    </div>
  );
}
